import { useCallback, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ExternalLink, Maximize2, Minimize2, PanelBottom, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import {
  useDetachedWindow,
  type DetachedWindowHandlers,
} from '@core/layout/janela-destacada/useDetachedWindow'
import ClashWorkbench from './ClashWorkbench'
import {
  CLASH_PANEL_MIN_H,
  CLASH_PANEL_MIN_W,
} from './store/clashSlice'

// ---------------------------------------------------------------------------
// Painel "Conflitos": o atalho do ribbon.
//
// E a MESMA ferramenta que a pagina do modulo (o `ClashWorkbench`), noutra
// moldura. Este ficheiro so trata da moldura: arrastar, redimensionar,
// maximizar, destacar para uma janela real do browser e fechar. Tudo o
// que e conflitos vive no banco de trabalho e no `clashSlice`.
//
// O painel MANTEM-SE depois de os conflitos passarem a modulo porque e o
// unico caminho em MODO LOCAL (sem backend nao ha separadores de modulos)
// e porque ligar o modulo exige `alembic upgrade head` e um clique no
// Admin. Ver docs/CLASH.md.
//
// JANELA DESTACADA. Reutiliza o `useDetachedWindow` das vistas 2D
// (window.open + clonagem das folhas de estilo + portal do React), com
// NOME PROPRIO: `vn2r-clash`. Nomes iguais reaproveitam a mesma janela do
// browser, por isso destacar os conflitos com o nome do 2D
// (`vn2r-views2d`) ou o dos modulos (`vn2r-module`) deitava abaixo o que
// la estivesse. A janela nasce do mesmo contexto JS, portanto partilha o
// store: clicar num conflito na janela de fora move a camara na janela
// principal e pinta o par, sem nada pelo meio.
// ---------------------------------------------------------------------------

const CLASH_WINDOW_NAME = 'vn2r-clash'
const CLASH_WINDOW_TITLE = 'VN2R openBIM Hub · Conflitos'
const CLASH_WINDOW_FEATURES =
  'width=1100,height=800,resizable=yes,scrollbars=yes'

export default function ClashPanel() {
  const open = useAppStore((s) => s.clashPanelOpen)
  const setOpen = useAppStore((s) => s.setClashPanelOpen)
  // O rectangulo escreve-se pelo store directo (`getState`) durante o
  // arrasto: assinar o setter aqui so servia para o React o passar a
  // callbacks que ja leem o estado fresco.
  const rect = useAppStore((s) => s.clashPanelRect)
  const maximized = useAppStore((s) => s.clashPanelMaximized)
  const setMaximized = useAppStore((s) => s.setClashPanelMaximized)
  const detached = useAppStore((s) => s.clashPanelDetached)
  const setDetached = useAppStore((s) => s.setClashPanelDetached)
  const { z, trazer } = useJanelaAberta('conflitos', open && !detached, 'canvas')
  const pushToast = useAppStore((s) => s.pushToast)

  const dragRef = useRef<{
    pointerStart: { x: number; y: number }
    rectStart: { x: number; y: number }
    /** Largura no momento em que se comecou a arrastar, para converter o
     *  "encostado a direita" (x = -1) numa posicao concreta. */
    startLeft: number
  } | null>(null)
  const resizeRef = useRef<{
    pointerStart: { x: number; y: number }
    sizeStart: { w: number; h: number }
  } | null>(null)
  const shellRef = useRef<HTMLDivElement | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (maximized) return
      e.preventDefault()
      const box = shellRef.current?.getBoundingClientRect()
      dragRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        rectStart: { x: rect.x, y: rect.y },
        startLeft: box ? box.left : Math.max(0, rect.x),
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [rect.x, rect.y, maximized],
  )

  const onResizeDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      resizeRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        sizeStart: { w: rect.w, h: rect.h },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [rect.w, rect.h],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current
      if (drag) {
        const dx = e.clientX - drag.pointerStart.x
        const dy = e.clientY - drag.pointerStart.y
        useAppStore.getState().setClashPanelRect({
          ...useAppStore.getState().clashPanelRect,
          x: drag.startLeft + dx,
          y: drag.rectStart.y + dy,
        })
      }
      const resize = resizeRef.current
      if (resize) {
        const dx = e.clientX - resize.pointerStart.x
        const dy = e.clientY - resize.pointerStart.y
        useAppStore.getState().setClashPanelRect({
          ...useAppStore.getState().clashPanelRect,
          w: Math.max(CLASH_PANEL_MIN_W, resize.sizeStart.w + dx),
          h: Math.max(CLASH_PANEL_MIN_H, resize.sizeStart.h + dy),
        })
      }
    }
    const onUp = () => {
      dragRef.current = null
      resizeRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [])

  const handlers = useMemo<DetachedWindowHandlers>(
    () => ({
      onBlocked: () => {
        setDetached(false)
        pushToast(
          'O browser bloqueou a janela dos Conflitos. Autoriza as janelas ' +
            'emergentes deste sítio (ícone à direita da barra de endereço) e ' +
            'tenta outra vez.',
          { type: 'error' },
        )
      },
      onClose: () => setDetached(false),
    }),
    [setDetached, pushToast],
  )

  // A janela so se abre com o painel aberto: destacar e uma forma de ver
  // o painel, nao um estado independente dele.
  const { container } = useDetachedWindow(open && detached, handlers, {
    name: CLASH_WINDOW_NAME,
    title: CLASH_WINDOW_TITLE,
    features: CLASH_WINDOW_FEATURES,
  })

  if (!open) return null

  if (detached) {
    if (!container) return null
    return createPortal(
      <>
        <div className="flex h-8 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs">
          <span className="truncate text-[var(--color-text-muted)]">
            VN2R openBIM Hub · Conflitos (janela destacada)
          </span>
          <button
            type="button"
            onClick={() => setDetached(false)}
            title="Voltar a mostrar o painel por cima do 3D"
            className="flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <PanelBottom size={11} />
            Voltar ao painel
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-bg)] text-[var(--color-text)]">
          <ClashWorkbench />
        </div>
      </>,
      container,
    )
  }

  // Maximizado ocupa o contentor todo (o viewport 3D), nao a janela: com
  // a barra lateral aberta, esticar a janela toda punha o painel a
  // comecar em x negativo.
  const style: React.CSSProperties = maximized
    ? { inset: '0.5rem' }
    : {
        top: rect.y,
        width: rect.w,
        height: rect.h,
        ...(rect.x < 0 ? { right: '0.75rem' } : { left: rect.x }),
      }

  return (
    <div
      ref={shellRef}
      data-testid="clash-panel"
      onPointerDownCapture={trazer}
      className="pointer-events-auto absolute flex max-w-[calc(100%-1rem)] flex-col overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-sm shadow-2xl"
      style={{ ...style, zIndex: z }}
    >
      <div
        onPointerDown={onTitleDown}
        className={`flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-2 ${
          maximized ? '' : 'cursor-grab select-none active:cursor-grabbing'
        }`}
      >
        <span className="font-medium">Conflitos</span>
        <div
          className="ml-auto flex shrink-0 items-center gap-1"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setDetached(true)}
            title="Destacar para uma janela do browser (segundo ecrã)"
            aria-label="Destacar os conflitos para uma janela do browser"
            className="rounded text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          >
            <ExternalLink size={13} />
          </button>
          <button
            type="button"
            onClick={() => setMaximized(!maximized)}
            title={maximized ? 'Restaurar o tamanho' : 'Maximizar'}
            aria-label={maximized ? 'Restaurar o painel' : 'Maximizar o painel'}
            aria-pressed={maximized}
            className="rounded text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          >
            {maximized ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
            title="Fechar"
            aria-label="Fechar painel de conflitos"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <ClashWorkbench />
      </div>

      {!maximized && (
        <div
          onPointerDown={onResizeDown}
          data-testid="clash-panel-resize"
          role="separator"
          aria-label="Redimensionar o painel de conflitos"
          className="absolute bottom-0 right-0 h-3.5 w-3.5 cursor-se-resize"
          style={{
            backgroundImage:
              'linear-gradient(135deg, transparent 50%, var(--color-text-muted) 50%)',
            opacity: 0.6,
          }}
          title="Redimensionar"
        />
      )}
    </div>
  )
}
